import { useCallback, useState } from 'react'

/**
 * A generic hook to handle modals using Promises.
 * @template T - The type of data returned by the modal.
 */
const useModalPromise = <T = boolean>() => {
  const [isVisible, setIsVisible] = useState(false)
  const [resolver, setResolver] = useState<((value: T) => void) | null>(null)

  // Opens the modal and returns a promise that resolves when the modal closes
  const openModalPromise = useCallback(() => {
    setIsVisible(true)
    return new Promise<T>((resolve) => {
      // We use a function inside setResolver to avoid React
      // interpreting the resolve function as a state updater
      setResolver(() => resolve)
    })
  }, [])

  // Resolves the promise with the provided value and hides the modal
  const handleClose = useCallback(
    (value: T) => {
      setIsVisible(false)
      if (resolver) {
        resolver(value)
        setResolver(null)
      }
    },
    [resolver]
  )

  return { isVisible, openModalPromise, handleClose }
}

export default useModalPromise
